import * as React from "react";
import { Alert, DialogTitle, MenuItem, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { oauthErrorMessage } from "@/hooks/api/use-oauth";
import { useImportZoomInfoAudience, useZoomInfoAudiences, type ZoomInfoImport } from "@/hooks/api/use-zoominfo";

export interface ZoomInfoImportDialogProps {
	open: boolean;
	pluginId: string;
	onClose: () => void;
	/** Receives an import session; the CSV column mapping takes it from there. */
	onImported: (result: ZoomInfoImport) => void;
}

export function ZoomInfoImportDialog({
	open,
	pluginId,
	onClose,
	onImported,
}: ZoomInfoImportDialogProps): React.JSX.Element {
	const [audienceId, setAudienceId] = React.useState("");
	const { data: audiences, isLoading, error } = useZoomInfoAudiences(pluginId, open);
	const importMutation = useImportZoomInfoAudience();
	const chosen = audiences?.find((audience) => audience.id === audienceId);
	// ZoomInfo charges a lookup only for a record it has not enriched for the account in the last 12 months.
	const cost =
		chosen &&
		`Checking Do Not Call status is free for contacts your ZoomInfo account has enriched in the last 12 months. ` +
			`Others use one ZoomInfo credit each: up to ${chosen.recordCount.toLocaleString()} for this audience.`;

	const handleClose = () => {
		onClose();
		setAudienceId("");
	};
	// Closing mid-import would still open column mapping when the request returns.
	const handleDismiss = () => {
		if (!importMutation.isPending) handleClose();
	};

	// Errors are toasted by the mutation; the dialog stays open to pick again.
	const handleImport = () => {
		importMutation.mutate(
			{ pluginId, audienceId },
			{
				onSuccess: (result) => {
					handleClose();
					onImported(result);
				},
			}
		);
	};

	return (
		<Dialog open={open} onClose={handleDismiss} fullWidth maxWidth="sm">
			<DialogTitle sx={{ p: 3, pb: 2 }}>
				<Stack spacing={1}>
					<Typography variant="h5">Import from ZoomInfo</Typography>
					<Typography color="text.secondary" variant="body2">
						Choose a ZoomInfo audience. You will map its columns to contact fields next.
					</Typography>
				</Stack>
			</DialogTitle>
			<DialogContent sx={{ px: 3 }}>
				{error ? (
					<Alert severity="error">{oauthErrorMessage(error)}</Alert>
				) : (
					<TextField
						select
						fullWidth
						label="Audience"
						value={audienceId}
						onChange={(event) => setAudienceId(event.target.value)}
						disabled={isLoading}
						helperText={audiences?.length === 0 ? "This ZoomInfo account has no contact audiences yet." : cost}
						sx={{ mt: 1 }}
					>
						{audiences?.map((audience) => (
							<MenuItem key={audience.id} value={audience.id} disabled={audience.importable === false}>
								{audience.name} ({audience.recordCount} contacts)
								{audience.importable === false && " - too large to import, split it in ZoomInfo"}
							</MenuItem>
						))}
					</TextField>
				)}
			</DialogContent>
			<DialogActions sx={{ p: 3, py: 2 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="secondary" onClick={handleClose} disabled={importMutation.isPending}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleImport} disabled={!audienceId} loading={importMutation.isPending}>
						Continue
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
